"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { UserCheck, PiggyBank, Zap, Target, ShieldCheck, Check, X, ArrowRight, Sparkles, MessageSquare } from "lucide-react";
const benefitsList = [{
  id: "1",
  title: "Direct Expert Partnership",
  badgeText: "Direct Access",
  agencyText: "Pitched by senior leads, then handed off to junior account managers.",
  consultantText: "You work 1:1 with me, Yassine Essaadi. Every strategy, audit, and optimization is executed directly by me.",
  impact: "No middleman miscommunication"
}, {
  id: "2",
  title: "Maximum Budget Efficiency",
  badgeText: "Cost Effective",
  agencyText: "30-50% of your retainer pays for agency office space, account executives, and corporate overhead.",
  consultantText: "100% of your investment goes straight into hands-on SEO expertise and measurable growth strategies.",
  impact: "Higher ROI per pound spent"
}, {
  id: "3",
  title: "Bespoke, Un-templated Strategy",
  badgeText: "Tailored Focus",
  agencyText: "Cookie-cutter audit templates and generic monthly deliverables across dozens of client accounts.",
  consultantText: "Custom SEO blueprints designed specifically for your target audience, site architecture, and business goals.",
  impact: "Zero bloated filler reports"
}, {
  id: "4",
  title: "Agile & Swift Execution",
  badgeText: "Rapid Speed",
  agencyText: "Slow response cycles gated by multiple layers of approvals and administrative red tape.",
  consultantText: "Direct communication via Slack or email. Fast technical turnarounds and immediate strategic shifts.",
  impact: "Accelerated execution timeline"
}, {
  id: "5",
  title: "Focused Quality Control",
  badgeText: "Dedicated Attention",
  agencyText: "Account managers handling 15-20 clients simultaneously with fragmented focus.",
  consultantText: "I limit my client capacity intentionally so your account receives my undivided attention and care.",
  impact: "Deep focus on your site"
}, {
  id: "6",
  title: "Honest & Transparent Reporting",
  badgeText: "Full Clarity",
  agencyText: "Vanity metrics and complicated reports designed to justify ongoing monthly retainers.",
  consultantText: "Clear revenue and lead-focused tracking with total visibility into exact tasks performed each month.",
  impact: "Clear business impact"
}];
export default function ConsultantVsAgency() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultantvsagencybenefits" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="absolute top-1/4 -right-20 size-96 bg-secondary/60 rounded-full blur-3xl pointer-events-none -z-10 opacity-70" />
      <div className="absolute bottom-10 -left-20 size-80 bg-muted/80 rounded-full blur-3xl pointer-events-none -z-10 opacity-60" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="secondary" className="mb-4 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-primary border border-accent/20 bg-secondary">
            <Sparkles className="size-3.5 text-accent mr-1 inline-block" />
            Direct Consultant vs Agency
          </Badge>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default mb-6">
            Why Working Directly With Me Outperforms standard SEO Agencies
          </h2>
          
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">
            When you hire YE Consultancy, you do not get passed off to junior account handlers or automated software templates. 
            You get direct, senior-level access to me—focused entirely on driving organic revenue for your business.
          </p>
        </div>

        <div className="bg-gradient-to-r from-card via-secondary/40 to-card border border-border/80 rounded-2xl p-6 sm:p-8 mb-16 shadow-xs relative">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary text-primary-foreground rounded-xl shrink-0 shadow-xs">
                <UserCheck className="size-6" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-1 font-default">
                  My Personal Commitment
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  {"\"I treat your business like my own. When you ask a question or need a complex technical SEO audit, I am the exact person analyzing your site and implementing the solution.\""}
                </p>
              </div>
            </div>
            <Button asChild size="lg" className="shrink-0 bg-primary text-primary-foreground hover:bg-primary/90 font-medium rounded-lg">
              <Link to="/ContactPage">
                Book a Free Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {benefitsList.map((item, i) => <motion.div key={item.id} variants={itemVariants} data-index={i} className="h-full">
              <Card className="h-full border border-border bg-card hover:border-accent/50 transition-[border-color,box-shadow] duration-300 shadow-sm hover:shadow-md flex flex-col justify-between rounded-xl overflow-hidden">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between mb-3">
                    <Badge variant="outline" className="bg-secondary/50 text-foreground border-accent/30 text-xs font-medium px-2.5 py-0.5">
                      {item.badgeText}
                    </Badge>
                    <span className="text-xs font-semibold text-accent flex items-center gap-1 bg-accent/10 px-2 py-0.5 rounded-md">
                      <Target className="size-3" />
                      {item.impact}
                    </span>
                  </div>
                  <CardTitle className="text-xl font-semibold text-foreground font-default">
                    {item.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="space-y-4 flex-1 text-sm">
                  <div className="p-3.5 bg-muted/60 rounded-lg border border-border/40">
                    <div className="flex items-center gap-2 font-medium text-destructive mb-1 text-xs uppercase tracking-wider">
                      <X className="size-3.5" />
                      Traditional SEO Agency
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      {item.agencyText}
                    </p>
                  </div>

                  <div className="p-3.5 bg-secondary/40 rounded-lg border border-accent/20">
                    <div className="flex items-center gap-2 font-semibold text-primary mb-1 text-xs uppercase tracking-wider">
                      <Check className="size-3.5 text-accent" />
                      Working With Me (Yassine)
                    </div>
                    <p className="text-foreground leading-relaxed font-medium">
                      {item.consultantText}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="bg-card border border-border rounded-2xl p-6 sm:p-10 shadow-sm mb-16">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <h3 className="text-2xl font-semibold text-foreground font-default mb-2">
              At a Glance: SEO Agency vs Independent Consultant
            </h3>
            <p className="text-sm text-muted-foreground">
              A transparent look at how my freelance consultancy model delivers greater focus and better value.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="py-3.5 px-4 font-semibold text-foreground">Feature / Approach</th>
                  <th className="py-3.5 px-4 font-semibold text-muted-foreground">Standard Agency</th>
                  <th className="py-3.5 px-4 font-semibold text-primary bg-secondary/30 rounded-t-lg">
                    YE Consultancy (Direct With Me)
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr>
                  <td className="py-3.5 px-4 font-medium text-foreground">Account Ownership</td>
                  <td className="py-3.5 px-4 text-muted-foreground">Junior Account Manager</td>
                  <td className="py-3.5 px-4 font-semibold text-foreground bg-secondary/10">
                    Yassine Essaadi (Senior Consultant)
                  </td>
                </tr>
                <tr>
                  <td className="py-3.5 px-4 font-medium text-foreground">Strategy Customisation</td>
                  <td className="py-3.5 px-4 text-muted-foreground">Templated monthly tasks</td>
                  <td className="py-3.5 px-4 font-semibold text-foreground bg-secondary/10">
                    100% Bespoke Search Blueprint
                  </td>
                </tr>
                <tr>
                  <td className="py-3.5 px-4 font-medium text-foreground">Client Load Per Lead</td>
                  <td className="py-3.5 px-4 text-muted-foreground">15 – 30 accounts per manager</td>
                  <td className="py-3.5 px-4 font-semibold text-foreground bg-secondary/10">
                    Strictly limited active client accounts
                  </td>
                </tr>
                <tr>
                  <td className="py-3.5 px-4 font-medium text-foreground">Budget Allocation</td>
                  <td className="py-3.5 px-4 text-muted-foreground">Split across agency overheads</td>
                  <td className="py-3.5 px-4 font-semibold text-foreground bg-secondary/10">
                    100% dedicated to SEO execution
                  </td>
                </tr>
                <tr>
                  <td className="py-3.5 px-4 font-medium text-foreground">Communication Channel</td>
                  <td className="py-3.5 px-4 text-muted-foreground">Ticketing system or delayed emails</td>
                  <td className="py-3.5 px-4 font-semibold text-foreground bg-secondary/10">
                    Direct Slack / WhatsApp / Email
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-primary text-primary-foreground rounded-2xl p-8 sm:p-12 text-center relative overflow-hidden shadow-lg">
          <div className="absolute -right-10 -bottom-10 size-60 bg-accent/20 rounded-full blur-2xl pointer-events-none" />
          
          <Badge variant="secondary" className="mb-4 bg-primary-foreground/10 text-primary-foreground border-none px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            Ready to grow your search revenue?
          </Badge>
          
          <h3 className="text-xl sm:text-2xl font-semibold font-default mb-4 max-w-2xl mx-auto">
            Experience the Difference of Direct SEO Consultancy
          </h3>
          
          <p className="text-primary-foreground/80 text-base max-w-xl mx-auto mb-8 leading-relaxed">
            Let us discuss your website, technical growth hurdles, and national or international expansion goals with an honest, non-salesy audit.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" variant="secondary" className="w-full sm:w-auto bg-background text-foreground hover:bg-background/90 font-medium px-8 py-6 rounded-lg text-base shadow-sm">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-2" />
              </Link>
            </Button>
            
            <Button asChild size="lg" variant="outline" className="w-full sm:w-auto border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 bg-transparent font-medium px-8 py-6 rounded-lg text-base">
              <Link to="/CaseStudiesPage">
                Explore Genuine Case Studies
              </Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}